import type { FC } from "react";

import { MAX_PAGE_OUTCOMES } from "components/constants";
import { AddOutcome } from "components/outcomes/AddOutcome";
import { sectionOutcomesToOptions } from "components/outcomes/helpers";
import { OptionsType } from "mds/components/TextListDropdown";
import { SquareIconButtonSize } from "mds/components/buttons/SquareIconButton";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectFullSectionOutcomes,
  selectNestedPageOutcomes,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";

interface AddOutcomePlusButtonProps {
  pageId: string;
  isDisabled: boolean;
  title?: string;
  iconButtonSize?: SquareIconButtonSize;
}

export const AddOutcomePlusButton: FC<AddOutcomePlusButtonProps> = ({
  pageId,
  isDisabled,
  title,
  iconButtonSize = "s",
}) => {
  const pageOutcomes = useListSelector(
    (s) => selectNestedPageOutcomes(s, pageId),
    ["section_outcome_id"],
  );
  const pageSectionOutcomeIds = pageOutcomes.map((pageOutcome) => pageOutcome.section_outcome_id);
  const { canAuthorSection } = useCapabilities();

  const sectionId = useAppSelector(selectCurrentSectionId);
  // Nested section outcomes (incl. outcome) needed for AddOutcome options.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const sectionOutcomes = useListSelector(selectFullSectionOutcomes);

  const pageSectionOutcomeIdSet = new Set(pageSectionOutcomeIds);

  const selectableSectionOutcomeOptions = sectionOutcomes.filter(
    (sectionOutcome) => !pageSectionOutcomeIdSet.has(sectionOutcome.id),
  );
  const options: OptionsType = sectionOutcomesToOptions(selectableSectionOutcomeOptions);

  if (!canAuthorSection) return null;

  const onSave = (sectionOutcomeIds: string[]) => {
    sectionOutcomeIds.forEach((sectionOutcomeId) => {
      storeApi.page_outcomes // TODO: Bulk create
        .create({ page_id: pageId, section_outcome_id: sectionOutcomeId, section_id: sectionId! });
    });
  };

  return (
    <div className="flex items-center gap-1">
      <AddOutcome
        hasReachedLimit={pageOutcomes.length === MAX_PAGE_OUTCOMES}
        iconButtonSize={iconButtonSize}
        isDisabled={isDisabled}
        limit={Math.max(MAX_PAGE_OUTCOMES - pageSectionOutcomeIds.length, 0)}
        options={options}
        title={title}
        iconOnly
        onSave={onSave}
      />
    </div>
  );
};
